import { memo, useMemo, useState } from 'react';
import { UnseenIndicator } from '@reflex/ui/components/shared/UnseenIndicator';
import { Tooltip, TooltipTrigger, TooltipContent } from '@reflex/ui/components/ui/tooltip';
import { SidebarItem } from '@reflex/ui/components/sidebar/SidebarItem';
import type { MenuActionItem } from '@reflex/ui/components/sidebar/SidebarItem';
import { Button } from '@reflex/ui/components/ui/button';
import {
  FolderInput,
  FolderMinus,
  FolderPlus,
  Pin,
  PinOff,
  Tag,
  Tags,
  Trash2,
  X,
} from 'lucide-react';
import { AgentActivityIcon } from '@/components/status/agent/AgentActivityIcon';
import { AgentOwnerAvatar } from '@/components/agent/AgentOwnerAvatar';
import { EditableAgentName } from '@/components/agent/EditableAgentName';
import { AgentMetaLine } from '@/components/agent/AgentMetaLine';
import { agentToMetaLineProps } from '@/components/agent/agent-meta-line-props';
import { primaryAgentAction } from '@/components/agent/agent-actions/agentLifecycleActions';
import { useAuth } from '@/hooks/auth/useAuth';
import { useAgentViews, isAgentUnseen } from '@/hooks/agents/useAgentViews';
import { backgroundWorkLabel, unseenActivityFor } from '@/hooks/agents/agent-activity-utils';
import { ACTIVITY_CONFIG } from '@/lib/status-colors';
import { useLiveAgentTurnSignals } from '@/hooks/agents/useLiveAgentTurnSignals';
import { useInterruptAgent } from '@/hooks/agents/useAgents';
import { useApplyAgentMark } from '@/hooks/agents/useApplyAgentMark';
import { useApplyAgentUnread } from '@/hooks/agents/useApplyAgentUnread';
import { useRemoveAgentsFromGroup } from '@/hooks/agents/useAgentGroups';
import { useRemoveAgentLabel } from '@/hooks/agents/useAgentLabels';
import { useAgentListFilterActions } from '@/components/agent/agent-list-filter-context';
import { AddToGroupDialog } from '@/components/agent/AddToGroupDialog';
import { groupActionLabel } from '@/components/agent/groupActionLabel';
import { AddLabelDialog } from '@/components/agent/AddLabelDialog';
import { agentGroupRef, agentGroupRefs, agentLabelRefs } from '@reflex/shared';
import { AGENT_MARK_CONFIG, agentStateChoices } from '@/lib/agent-marks';
import { timeAgo } from '@reflex/ui/lib/format';
import { cn } from '@reflex/ui/lib/utils';
import type { Agent, AgentActivity } from '@/lib/types';
import type { SidebarSectionContext } from '@/lib/agent-utils';

export interface ActiveAgentRowProps {
  agent: Agent;
  selected: boolean;
  activity: AgentActivity;
  onSelect: (agent: Agent) => void;
  onArchive: (agent: Agent) => void;
  onDelete: (agent: Agent) => void;
  onTogglePin?: (agent: Agent) => void;
  /** When set, replaces default row chrome while unselected (e.g. inbox attention tint). */
  highlight?: string;
  /** Optional status caption below the title row (inbox-style). */
  statusCaption?: { text: string; className: string };
  /** Optional muted line under the status (e.g. prompt preview). */
  subtitle?: string;
  /** Multi-select mode is active: show a checkbox and toggle selection on click. */
  selectionMode?: boolean;
  /** Whether this row is checked in the current multi-selection. */
  checked?: boolean;
  /** Toggle this row's selection; `shiftKey` supports range selection. */
  onToggleSelect?: (agent: Agent, shiftKey: boolean) => void;
  /**
   * The taxonomy section this row is being shown under (the group or label
   * view). When unset, the removal actions follow the list's active group and
   * label filters instead, which is the other way you end up looking at one.
   */
  sectionContext?: SidebarSectionContext | null;
}

export const ActiveAgentRow = memo(function ActiveAgentRow({
  agent,
  selected,
  activity,
  onSelect,
  onArchive,
  onDelete,
  onTogglePin,
  highlight,
  statusCaption,
  subtitle,
  selectionMode = false,
  checked = false,
  onToggleSelect,
  sectionContext,
}: ActiveAgentRowProps) {
  // Primary affordance comes from the shared `primaryAgentAction` model the
  // stream header and detail panel also use. Both surfaces derive `activity`
  // from the durable turn state, so the row and the panel beside it always
  // agree. This row only ever renders non-archived agents, so `primary` is
  // Stop (while working) or Archive.
  const primary = primaryAgentAction(agent, activity);
  const interrupt = useInterruptAgent();
  const setMark = useApplyAgentMark();
  const markUnread = useApplyAgentUnread();
  const { user: currentUser } = useAuth();
  const views = useAgentViews();
  const turnSignals = useLiveAgentTurnSignals();
  const showOwnerAvatar = !!agent.userId && agent.userId !== currentUser?.id;
  // The dot only makes sense on a row you are not already looking at: opening
  // the agent marks it seen, so the selected row never carries it. It reads
  // the evidence-gated `unseenActivityFor`, not the display `activity`: the
  // display defaults a `running` agent with a `null` turn state to idle, but
  // the dot needs proof a turn actually finished (`lastActiveAt` advances on
  // in-flight tool calls). Same rule as `isAgentUnread`, so the dot and the
  // Unread facet agree.
  const unseen = !selected && isAgentUnseen(views, agent, unseenActivityFor(agent, turnSignals));
  // Renaming is a deliberate act on the row you already have open: keep the
  // first click on an unselected row as "open the agent", and only turn the
  // title into a click-to-edit field once this row is the selected one. Never
  // while picking a multi-selection, where a row click toggles the checkbox.
  const canEditName = selected && !selectionMode;
  // A finished turn with background work still running reads as working
  // (`activityFromDurableAgentState`); the caption says why, in place of
  // whatever caller-supplied status caption the row would otherwise show.
  const backgroundLabel = backgroundWorkLabel(agent);
  const caption = backgroundLabel
    ? { text: backgroundLabel, className: ACTIVITY_CONFIG.working.sidebar.className }
    : statusCaption;

  // Which group or label the removal actions would act on: for a group, the
  // section this row is rendered under, else whatever the list is filtered to;
  // for a label, only the filter, since labels have no section of their own.
  // Either way it has to be one this agent actually carries, so an action never
  // appears where it would be a no-op — and the two are resolved separately,
  // because removing a group membership and taking a label off are different
  // writes.
  const listFilter = useAgentListFilterActions();
  const removeFromGroup = useRemoveAgentsFromGroup();
  const removeLabel = useRemoveAgentLabel();
  const [addToGroupOpen, setAddToGroupOpen] = useState(false);
  const [addLabelOpen, setAddLabelOpen] = useState(false);
  const sectionGroupId = sectionContext?.kind === 'group' ? sectionContext.id : null;
  const activeGroup = useMemo(() => {
    const targetId = sectionGroupId ?? listFilter?.activeGroupId ?? null;
    if (!targetId) return null;
    return agentGroupRefs(agent).find((g) => g.id === targetId) ?? null;
  }, [agent, sectionGroupId, listFilter]);
  const activeLabel = useMemo(() => {
    const targetId = listFilter?.activeLabelId ?? null;
    if (!targetId) return null;
    return agentLabelRefs(agent).find((l) => l.id === targetId) ?? null;
  }, [agent, listFilter]);

  const menuActions: MenuActionItem[] = useMemo(
    () => [
      ...(onTogglePin
        ? [
            {
              label: agent.pinned ? 'Unpin' : 'Pin',
              icon: agent.pinned ? PinOff : Pin,
              onClick: () => onTogglePin(agent),
            },
          ]
        : []),
      // Filing actions: which group the run belongs to, and what kind of work
      // it is. Three icon families, one per idea — a folder you file a run
      // into, a tag you stick on it, and the state picker's own `Tag` below.
      // `Tags` (plural) keeps the label action distinct from that picker,
      // which would otherwise read as two ways into the same thing.
      // A run is in one group, so once it is filed the same entry is a move,
      // and says so before it is clicked.
      {
        label: groupActionLabel([agent]),
        icon: agentGroupRef(agent) ? FolderInput : FolderPlus,
        onClick: () => setAddToGroupOpen(true),
      },
      { label: 'Add label', icon: Tags, onClick: () => setAddLabelOpen(true) },
      ...(activeGroup
        ? [
            {
              label: `Remove from ${activeGroup.name}`,
              icon: FolderMinus,
              onClick: () =>
                removeFromGroup.mutate({ groupId: activeGroup.id, agentIds: [agent.id] }),
            },
          ]
        : []),
      ...(activeLabel
        ? [
            {
              label: `Remove ${activeLabel.name}`,
              icon: X,
              onClick: () => removeLabel.mutate({ labelId: activeLabel.id, agentIds: [agent.id] }),
            },
          ]
        : []),
      // One flat picker for the run's state: the marks, Archived (a peer state
      // rather than a separate control), and Unread (per-viewer read state, the
      // one entry that doesn't displace the run's mark). Ordering, labels, and
      // colors come from the shared `agentStateChoices` so this menu and the
      // agent action list can't drift.
      {
        label: agent.mark ? AGENT_MARK_CONFIG[agent.mark].label : 'Mark as',
        icon: agent.mark ? AGENT_MARK_CONFIG[agent.mark].icon : Tag,
        iconClassName: agent.mark
          ? cn(AGENT_MARK_CONFIG[agent.mark].className, 'icon-full-bleed')
          : undefined,
        items: agentStateChoices(agent.mark).map((choice) => ({
          label: choice.label,
          icon: choice.icon,
          iconClassName: choice.iconClassName,
          checked: choice.checked,
          onClick: () => {
            if (choice.kind === 'archive') return onArchive(agent);
            if (choice.kind === 'unread') return markUnread(agent);
            // Re-picking the applied mark clears it, so applying and removing
            // are the same gesture.
            return setMark(agent, choice.checked ? null : choice.mark);
          },
        })),
      },
      { separator: true as const },
      { label: 'Delete', icon: Trash2, onClick: () => onDelete(agent), destructive: true },
    ],
    [
      agent,
      onArchive,
      onDelete,
      onTogglePin,
      setMark,
      markUnread,
      activeGroup,
      removeFromGroup,
      activeLabel,
      removeLabel,
    ],
  );

  return (
    <>
      <SidebarItem
        testId={`agent-sidebar-row-${agent.id}`}
        selected={selected}
        onClick={() => onSelect(agent)}
        selectable
        selectionMode={selectionMode}
        checked={checked}
        onToggleSelect={(shiftKey) => onToggleSelect?.(agent, shiftKey)}
        highlight={highlight}
        icon={<AgentActivityIcon activity={activity} mark={agent.mark} agentId={agent.id} />}
        actions={
          <>
            {onTogglePin && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={agent.pinned ? 'Unpin' : 'Pin'}
                    onClick={() => onTogglePin(agent)}
                  >
                    {agent.pinned ? (
                      <PinOff className="h-3.5 w-3.5" />
                    ) : (
                      <Pin className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{agent.pinned ? 'Unpin' : 'Pin'}</TooltipContent>
              </Tooltip>
            )}
            {/* Primary row affordance mirrors the stream header: Stop while the
              agent is working, Archive otherwise. The kind + label + icon come
              from the shared `primaryAgentAction`; Stop interrupts the current
              turn (leaving the sandbox up) rather than tearing it down. Sandbox
              teardown lives only in Delete. */}
            {primary.kind === 'stop' ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={primary.label}
                    onClick={() => interrupt.mutate(agent.id)}
                  >
                    <primary.icon className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{primary.label}</TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={primary.label}
                    onClick={() => onArchive(agent)}
                  >
                    <primary.icon className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{primary.label}</TooltipContent>
              </Tooltip>
            )}
          </>
        }
        menuActions={menuActions}
      >
        <>
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1.5">
              {showOwnerAvatar && agent.userId && <AgentOwnerAvatar userId={agent.userId} />}
              {/* When editable, stop the edit click (and the input's clicks while
                renaming) from bubbling to the row's open/select handler. When
                read-only the wrapper is transparent so a title click still
                opens the agent. Shift-click always falls through: it is the
                gesture that starts a multi-selection from the row.

                `flex` rather than a plain inline wrapper: selecting the row
                swaps the bare title for `InlineEditableText`'s `inline-flex`
                shell, and an inline formatting context aligns that shell on the
                strut's baseline, adding ~2px of leading above it. The row's
                icon is pinned to a fixed-height slot at the top, so that
                leading pushed the title down and left the icon visibly high on
                exactly the row you had open. A flex container has no strut, so
                the title sits at the same y in both states. */}
              <span
                className="flex min-w-0"
                onClick={
                  canEditName
                    ? (e) => {
                        if (!e.shiftKey) e.stopPropagation();
                      }
                    : undefined
                }
                onKeyDown={canEditName ? (e) => e.stopPropagation() : undefined}
              >
                <EditableAgentName
                  agentId={agent.id}
                  name={agent.name}
                  disabled={!canEditName}
                  as="h3"
                  size="sm"
                  className={cn(
                    'min-w-0',
                    unseen ? 'font-semibold text-foreground' : 'font-normal',
                  )}
                />
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {unseen && <UnseenIndicator />}
              <span className="text-3xs text-muted-foreground">
                {timeAgo(agent.createdAt, { compact: true })}
              </span>
            </div>
          </div>
          {caption ? (
            <p className={cn('text-2xs truncate', caption.className)}>{caption.text}</p>
          ) : null}
          {subtitle ? (
            <p className="mt-0.5 text-3xs text-muted-foreground leading-tight truncate">
              {subtitle}
            </p>
          ) : null}
          <AgentMetaLine {...agentToMetaLineProps(agent)} />
        </>
      </SidebarItem>
      {/* Rendered by the row rather than the sidebar so every surface that
          shows a row (sessions list, pinned section, inbox) gets the filing
          actions without threading a handler through each of them. */}
      <AddToGroupDialog open={addToGroupOpen} onOpenChange={setAddToGroupOpen} agents={[agent]} />
      <AddLabelDialog open={addLabelOpen} onOpenChange={setAddLabelOpen} agentIds={[agent.id]} />
    </>
  );
});
